import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import {
  getAllGroupSchoolAdminService,
  createGroupSchoolAdminService,
  updateGroupSchoolAdminService,
  deleteGroupSchoolAdminService,
  getGroupSchoolAdminService,
} from "@/service/group-school-admin.service";
import {
  GroupSchoolAdminModel,
  GroupSchoolAdminQuery,
} from "@/types/group-school-admin.model";
import type { AxiosError } from "axios";
import { ROLES, successToast } from "@/utils/constants";
import { fetchDashboardData } from "./dashboardSlice";

interface GroupSchoolAdminState {
  groupSchoolAdmins: GroupSchoolAdminModel[];
  currentGroupSchoolAdmin: GroupSchoolAdminModel | null;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  totalItems: number;
  noGroupSchoolAdmins: boolean;
  query: GroupSchoolAdminQuery;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

const initialState: GroupSchoolAdminState = {
  groupSchoolAdmins: [],
  noGroupSchoolAdmins: true,
  currentGroupSchoolAdmin: null,
  loading: true,
  actionLoading: false,
  error: null,
  totalItems: 0,
  query: {
    page: 1,
    pageSize: 10,
    search: "",
    fromDate: "",
    toDate: "",
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
  },
};

// Async Actions
export const fetchGroupSchoolAdmins = createAsyncThunk(
  "groupSchoolAdmin/fetchAll",
  async (query: GroupSchoolAdminQuery, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const {
        data: { data, pageInfo },
      } = await getAllGroupSchoolAdminService(query);
      dispatch(setLoading(false));
      dispatch(setNoGroupSchoolAdmins(false));
      dispatch(setQuery(query));

      return {
        groupSchoolAdmins: data,
        pagination: {
          totalItems: pageInfo?.total_count as number,
          totalPages: pageInfo?.total_pages as number,
          currentPage: pageInfo?.current_page as number,
          pageSize: pageInfo?.per_page as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoGroupSchoolAdmins(true));
        throw error;
      }

      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch groupSchoolAdmins";
      dispatch(setError(message));
      throw error;
    }
  }
);

export const fetchGroupSchoolAdminById = createAsyncThunk(
  "groupSchoolAdmin/fetchById",
  async (id: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await getGroupSchoolAdminService(id);
      dispatch(setLoading(false));
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch groupSchoolAdmin";
      dispatch(setError(message));
      throw error;
    }
  }
);

export const createGroupSchoolAdmin = createAsyncThunk(
  "groupSchoolAdmin/create",
  async (groupSchoolAdminData: Partial<GroupSchoolAdminModel>, { dispatch }) => {
    try {
      dispatch(setActionLoading(true));
      const response = await createGroupSchoolAdminService(groupSchoolAdminData as GroupSchoolAdminModel);

      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to create groupSchoolAdmin";
      dispatch(setError(message));
      throw error;
    }
  }
);

export const updateGroupSchoolAdmin = createAsyncThunk(
  "groupSchoolAdmin/update",
  async (
    { id, groupSchoolAdminData }: { id: string; groupSchoolAdminData: Partial<GroupSchoolAdminModel> },
    { dispatch }
  ) => {
    try {
      dispatch(setActionLoading(true));
      // Ensure the groupSchoolAdmin object has the id property
      const groupSchoolAdminToUpdate = { ...groupSchoolAdminData };
      const response = await updateGroupSchoolAdminService(
        id,
        groupSchoolAdminToUpdate as GroupSchoolAdminModel
      );

      dispatch(setActionLoading(false));
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to update groupSchoolAdmin";
      dispatch(setError(message));
      throw error;
    }
  }
);

export const deleteGroupSchoolAdmin = createAsyncThunk(
  "groupSchoolAdmin/delete",
  async (id: string, { dispatch, getState }) => {
    try {
      dispatch(setActionLoading(true));
      await deleteGroupSchoolAdminService(id);

      successToast("Group School Admin deleted successfully", "");

      const state = getState() as RootState;
      const userRole = state.auth.user?.role;

      const isSuperAdmin = userRole?.code === ROLES.SUPER_ADMIN;

      if (isSuperAdmin) {
        dispatch(fetchDashboardData());
      }

      dispatch(
        fetchGroupSchoolAdmins({
          page: state.groupSchoolAdmins.pagination.currentPage,
          pageSize: state.groupSchoolAdmins.pagination.pageSize,
        })
      );

      dispatch(setActionLoading(false));
      return id;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to delete groupSchoolAdmin";
      dispatch(setError(message));
      throw error;
    }
  }
);

const groupGroupSchoolAdminSlice = createSlice({
  name: "groupSchoolAdmin",
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<GroupSchoolAdminQuery>) => {
      state.query = action.payload;
    },
    setNoGroupSchoolAdmins: (state, action: PayloadAction<boolean>) => {
      state.noGroupSchoolAdmins = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setActionLoading: (state, action: PayloadAction<boolean>) => {
      state.actionLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
      state.actionLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetCurrentGroupSchoolAdmin: (state) => {
      state.currentGroupSchoolAdmin = null;
    },
    resetAllState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch all groupSchoolAdmins
      .addCase(fetchGroupSchoolAdmins.fulfilled, (state, action) => {
        state.groupSchoolAdmins = action.payload.groupSchoolAdmins;
        state.pagination = action.payload.pagination;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchGroupSchoolAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch groupSchoolAdmins";
      })

      // Fetch groupSchoolAdmin by id
      .addCase(fetchGroupSchoolAdminById.fulfilled, (state, action) => {
        state.currentGroupSchoolAdmin = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchGroupSchoolAdminById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch groupSchoolAdmin";
      })

      // Create groupSchoolAdmin
      .addCase(createGroupSchoolAdmin.fulfilled, (state) => {
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(createGroupSchoolAdmin.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to create groupSchoolAdmin";
      })

      // Update groupSchoolAdmin
      .addCase(updateGroupSchoolAdmin.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(updateGroupSchoolAdmin.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to update groupSchoolAdmin";
      })

      // Delete groupSchoolAdmin
      .addCase(deleteGroupSchoolAdmin.fulfilled, (state) => {
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(deleteGroupSchoolAdmin.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to delete groupSchoolAdmin";
      })
  },
});

export const {
  setLoading,
  setActionLoading,
  setError,
  clearError,
  resetCurrentGroupSchoolAdmin,
  resetAllState,
  setNoGroupSchoolAdmins,
  setQuery,
} = groupGroupSchoolAdminSlice.actions;

// Selectors
export const selectGroupSchoolAdmins = (state: RootState) => state.groupSchoolAdmins.groupSchoolAdmins;
export const selectCurrentGroupSchoolAdmin = (state: RootState) =>
  state.groupSchoolAdmins.currentGroupSchoolAdmin;
export const selectGroupSchoolAdminLoading = (state: RootState) => state.groupSchoolAdmins.loading;
export const selectGroupSchoolAdminActionLoading = (state: RootState) =>
  state.groupSchoolAdmins.actionLoading;
export const selectGroupSchoolAdminError = (state: RootState) => state.groupSchoolAdmins.error;
export const selectTotalItems = (state: RootState) =>
  state.groupSchoolAdmins.pagination.totalItems;
export const selectNoGroupSchoolAdmins = (state: RootState) => state.groupSchoolAdmins.noGroupSchoolAdmins;
export const selectQuery = (state: RootState) => state.groupSchoolAdmins.query;

export default groupGroupSchoolAdminSlice.reducer;
